import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { View, XStack, YStack } from 'tamagui';
import { ShieldOff as BlockedIcon } from 'lucide-react-native';

import { Text } from '@/components/text';
import { EmptyState } from '@/components/empty-state';
import { SolidSurface, useGlassAccentColor } from '@/components/glass-surface';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ResourceListView } from '@/components/resource-list-view';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { Toast } from '@/components/toast';
import { WebRefreshButton } from '@/components/web-refresh';
import { useBlockedUsers } from '@/hooks/use-blocked-users';
import { usePaginatedResource } from '@/hooks/use-paginated-resource';
import { api, type BlockedUser, type BlockedUsersPage } from '@/lib/api';
import { displayHandle } from '@/lib/avatar-identity';
import { queryKeys } from '@/lib/query-keys';
import { space } from '@/theme/tokens.generated';

function selectAllBlockedUsers(pages: BlockedUsersPage[] | undefined): BlockedUser[] {
  return pages?.flatMap((page) => page.items) ?? [];
}

// Same window/reasoning as reveal-requests's own ConnectionRow "Revoke
// connection & block" button (issue #253) -- review on this issue (#101)
// pointed out a real gap the first version of this screen missed: a
// block sourced from `revoke`/`decline_threshold` (domain.BlockSource)
// exists because of an actual behavioral signal (a revoke, or repeated
// declines), not a one-off manual choice, and reopens the SAME contact
// surface Safety Center's own copy describes ("they can't see your
// threads, and they can't request a reveal again"). Applied uniformly
// rather than branching on `source` -- one action, one treatment,
// everywhere, matching this app's own token-by-intent consistency rule,
// and a mis-tap on a plain manual block loses nothing by also getting a
// 5s recovery window.
const UNDO_WINDOW_MS = 5000;

type PendingUndo = { message: string; onUndo: () => void };

// Issue #101: api.deleteBlock (DELETE /blocks/{userId}) existed with
// zero call sites in the app -- the block confirm screen originally
// promised "you can undo this later", which was false until this
// screen existed. Reached from Settings and Safety Center, not the
// Profile tab (which is for editing what OTHER people see, not account
// housekeeping -- matches Safety Center's own placement).
//
// Every row here is something the caller made (GET /me/blocks is
// blocker-only, per its own backend doc comment) and can always
// unblock via DELETE /blocks/{userId} regardless of the block's
// source -- no "this can't actually be removed" dead-end case exists.
export function BlockedUsers() {
  const router = useRouter();
  const blockedUsersQuery = useBlockedUsers();
  const blockedUsers = usePaginatedResource(blockedUsersQuery, selectAllBlockedUsers);
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();

  const [undoToast, setUndoToast] = useState<PendingUndo | null>(null);
  const showUndoToast = (message: string, onUndo: () => void) => setUndoToast({ message, onUndo });

  return (
    <ScreenContainer scrollUnderDock>
      <YStack flex={1}>
        <YStack pt="$4" pb="$3">
          <ScreenHeader
            title="Blocked users"
            subtitle="People you've blocked. Unblock anyone here."
            action={
              <WebRefreshButton onRefresh={() => blockedUsers.refetch()} refreshing={blockedUsers.isRefetching} />
            }
            onBack={() => router.back()}
          />
        </YStack>

        <ResourceListView
          resource={blockedUsers}
          errorTitle="Couldn't load blocked users"
          renderItem={({ item }) => <BlockedUserRow blockedUser={item} onShowUndoToast={showUndoToast} />}
          emptyNode={<EmptyBlockedUsersList />}
          listProps={{
            style: { flex: 1 },
            ...dockScroll,
            keyExtractor: (b) => b.blockId,
            contentContainerStyle:
              blockedUsers.items.length === 0
                ? { flexGrow: 1, paddingBottom: space[6] + dockClearance }
                : {
                    gap: space[2],
                    paddingBottom: (undoToast ? space[6] + space[16] + space[8] : space[6]) + dockClearance,
                  },
          }}
        />

        {/* Same floating-overlay placement as reveal-requests's own
            toast -- see that screen's doc comment for why (standard
            snackbar positioning, stable scroll position, the dock clearance
            applied here since this overlay sits outside
            ScreenContainer's own padding flow). */}
        {undoToast && (
          <View position="absolute" left={space[4]} right={space[4]} bottom={dockClearance + space[4]}>
            <Toast
              variant="info"
              message={undoToast.message}
              durationMs={UNDO_WINDOW_MS}
              onDismiss={() => setUndoToast(null)}
              action={{
                label: 'Undo',
                onPress: () => {
                  undoToast.onUndo();
                  setUndoToast(null);
                },
              }}
            />
          </View>
        )}
      </YStack>
    </ScreenContainer>
  );
}

function EmptyBlockedUsersList() {
  const accentColor = useGlassAccentColor();

  return (
    <EmptyState
      icon={<BlockedIcon size={24} strokeWidth={1.75} color={accentColor} />}
      title="No blocked users"
      description="Anyone you block will show up here, so you can unblock them if things change."
    />
  );
}

function BlockedUserRow({
  blockedUser,
  onShowUndoToast,
}: {
  blockedUser: BlockedUser;
  onShowUndoToast: (message: string, onUndo: () => void) => void;
}) {
  const queryClient = useQueryClient();
  // See reveal-requests's ConnectionRow's own pendingRevoke/
  // revokeCommitTimeoutRef for why this isn't cleared on unmount
  // (issue #253): armed means it commits, unless explicitly undone.
  const [pendingUnblock, setPendingUnblock] = useState(false);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const unblock = useMutation({
    mutationFn: () => api.deleteBlock(blockedUser.blockedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.blockedUsers() });
    },
  });

  // Row disappears the instant the button is pressed -- same
  // optimistic-hide-plus-toast pattern as ConnectionRow's revoke path.
  if (pendingUnblock) return null;

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <XStack alignItems="center" gap="$3">
        <PseudonymousAvatar handle={blockedUser.blockedUserHandle} id={blockedUser.blockedUserId} size={40} />
        <YStack flex={1} gap="$1">
          <Text color="$color" fontWeight="$semibold" numberOfLines={1}>
            {displayHandle(blockedUser.blockedUserHandle)}
          </Text>
          {unblock.isError && (
            <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
              {unblock.error.message || 'Something went wrong. Check your connection and try again.'}
            </Text>
          )}
        </YStack>
        <PrimaryButton
          size="sm"
          onPress={() => {
            if (pendingUnblock) return;
            setPendingUnblock(true);
            commitTimeoutRef.current = setTimeout(() => unblock.mutate(), UNDO_WINDOW_MS);
            onShowUndoToast(`Unblocked ${displayHandle(blockedUser.blockedUserHandle)}`, () => {
              if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
              setPendingUnblock(false);
            });
          }}
        >
          Unblock
        </PrimaryButton>
      </XStack>
    </SolidSurface>
  );
}
